import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { feedbackApi } from '@/api/feedback';

const feedbackSchema = z.object({
  nps_score: z.number().min(0).max(10),
  feedback_text: z.string().max(1000).optional(),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FeedbackDialog = ({ open, onOpenChange }: FeedbackDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      nps_score: 5,
      feedback_text: '',
    },
  });

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);
    try {
      await feedbackApi.submitFeedback({
        nps_score: data.nps_score,
        feedback_text: data.feedback_text || null,
      });

      // Track feedback submission
      if (typeof window !== 'undefined' && window.plausible) {
        window.plausible('Feedback Submitted', {
          props: {
            nps_score: data.nps_score.toString(),
            has_text: data.feedback_text ? 'true' : 'false'
          }
        });
      }

      toast({
        title: "Thank you for your feedback!",
        description: "Your feedback helps us improve Arcitext.",
      });

      form.reset();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error submitting feedback",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const npsScore = form.watch('nps_score');

  const getNpsLabel = (score: number) => {
    if (score <= 6) return 'Not likely';
    if (score <= 8) return 'Neutral';
    return 'Extremely likely';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share Your Feedback</DialogTitle>
          <DialogDescription>
            Help us improve Arcitext by sharing your thoughts and rating your experience.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="nps_score"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    How likely are you to recommend Arcitext to others?
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-3">
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>0 - Not likely</span>
                        <span className="font-medium">{npsScore} - {getNpsLabel(npsScore)}</span>
                        <span>10 - Extremely likely</span>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="feedback_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional feedback (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tell us what you love about Arcitext or how we can improve..."
                      {...field}
                      maxLength={1000}
                      rows={4}
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground text-right">
                    {field.value?.length || 0}/1000
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};